import React, { useEffect, useState } from "react";
import { inject, observer } from "mobx-react";
import { Utils } from "@/common/utils";
import { Alert, Button, Spinner } from "react-bootstrap";
import DebouncedInput from "Components/Common/DebouncedInput";
import { CreateStrategicPlanForm } from "./createStrategicPlan";
import { EditableStrategicPlanForm } from "./editStrategicPlan";
import { StrategicPlanParent, StrategicPlanItem, StrategicPlanStore } from "Stores/strategicPlanStore";
import { StyledModal } from "Components/Common/StyledModal";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { IngestionStore } from "Stores/index";
import { CustomFormData } from "./interfaces";
import PageTitle from "Components/Common/PageTitle";
export type StatusType = "open" | "achieved" | "unmet" | "no_date_assigned" | "overdue";
interface StrategicPlansProps {
    strategicPlans?: StrategicPlanParent[] | undefined;
}

interface StrategicStores {
    strategicPlanStore?: StrategicPlanStore;
    ingestionStore?: IngestionStore;
}

@inject("strategicPlanStore")
@observer
export class StrategicPlansScreen extends React.Component<StrategicStores, StrategicPlansProps> {
    async componentDidMount() {
        Utils.setViewName("Institutional Effectiveness Plans", "Institutional Effectiveness Plans");
        if (this.props.strategicPlanStore) { await this.props.strategicPlanStore.fetchStrategicPlanItems(); }
    }

    handleLoadStrategicPlanItems = async () => {
        if (!this.props.strategicPlanStore) return;
        await this.props.strategicPlanStore.fetchStrategicPlanItems();
    };
    handleArchivePlanItem = (planItem) => {
        if (!this.props.strategicPlanStore) return;
        this.props.strategicPlanStore.archiveStrategicPlanItem(planItem.id, planItem.status, planItem.strategic_plan_ruuid).then(
            async () => {
                if (this.props.strategicPlanStore) {
                    await this.props.strategicPlanStore.fetchStrategicPlanItems();
                }
            });
    };

    handleCreateStrategicPlan = (strategicPlan) => {
        if (!this.props.strategicPlanStore) return;
        this.props.strategicPlanStore.createStrategicPlan({
            name: strategicPlan.name,
            status: strategicPlan?.status,
            start_date: strategicPlan.start_date,
            end_date: strategicPlan.start_date
        }).then(
            async () => {
                if (this.props.strategicPlanStore) {
                    await this.props.strategicPlanStore.fetchStrategicPlanItems();
                }
            });
    };

    handleArchivePlan = (planItem) => {
        if (!this.props.strategicPlanStore) return;
        this.props.strategicPlanStore.archiveStrategicPlan(planItem.ruuid, planItem.status).then(
            async () => {
                if (this.props.strategicPlanStore) {
                    await this.props.strategicPlanStore.fetchStrategicPlanItems();
                }
            });
    };

    handleDeletePlanItem = (planItem) => {
        if (!this.props.strategicPlanStore) return;
        this.props.strategicPlanStore.deleteStrategicPlanItem(planItem.id)
            .then(async () => {
                if (this.props.strategicPlanStore) {
                    await this.props.strategicPlanStore.fetchStrategicPlanItems();
                }
            });
    };

    handleCopyPlan = (plan: StrategicPlanParent) => {
        if (!this.props.strategicPlanStore) return;
        this.props.strategicPlanStore.copyStrategicPlan(plan.name, plan.ruuid)
            .then(async () => {
                if (this.props.strategicPlanStore) {
                    await this.props.strategicPlanStore.fetchStrategicPlanItems();
                }
            });
    };

    render() {
        if (!this.props.strategicPlanStore || !this.props.strategicPlanStore.strategicPlanItems) {
            return null;
        }
        return (
            <>
                <PageTitle pageTitle={"Institutional Effectiveness Plans"} />
                <StrategicPlans
                    strategicPlans={this.props.strategicPlanStore.strategicPlanItems}
                    onArchiveItem={(planItem) => { this.handleArchivePlanItem(planItem); }}
                    onArchivePlan={(planItem) => { this.handleArchivePlan(planItem); }}
                    onDelete={(planItem) => { this.handleDeletePlanItem(planItem); }}
                    onCopyPlan={(plan) => { this.handleCopyPlan(plan); }}
                    strategicPlanStore={this.props.strategicPlanStore}
                    loadStrategicPlans={async () => await this.handleLoadStrategicPlanItems()}
                />
            </>
        );
    }
}

interface StrategicPlansProps {
    strategicPlans?: StrategicPlanParent[] | undefined,
    onArchiveItem: (e: StrategicPlanItem) => void,
    onArchivePlan: (e: StrategicPlanParent) => void,
    onDelete: (e: StrategicPlanItem) => void,
    onCopyPlan: (e: StrategicPlanParent) => void,
    strategicPlanStore: StrategicPlanStore
    loadStrategicPlans: () => Promise<void>;
}

function StrategicPlans({ strategicPlans, onArchiveItem, onArchivePlan, onDelete, onCopyPlan, strategicPlanStore, loadStrategicPlans }: StrategicPlansProps) {
    const [plans, setPlans] = useState<StrategicPlanParent[]>(strategicPlans || []);
    const [searchBarValue, setSearchBarValue] = useState("");
    const [onEdit, setOnEdit] = useState(false);
    const [dndChanged, setDndChanged] = useState(false);
    const [datesChanged, setDatesChanged] = useState(false);
    const [openCancel, setOpenCancel] = useState(false);
    const [createItem, setCreateItem] = useState(false);
    const [alertContent, setAlertContent] = useState({ variant: "light", message: "" });
    const [alertProcessing, setAlertProcessing] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    const PlanStatus = ({ status }: { status: string }) => {
        return (
            <div className="status" data-testid="status-sp">
                <i className={`${status} icon-circle icon`} />
                {status.replace(/(^\w{1})|(_+\w{1})/g, letter => letter.toUpperCase()).replace(/_/g, " ")}
            </div>
        );
    };

    useEffect(() => {
        setPlans(strategicPlans || []);
        if (initialLoading && strategicPlans) {
            setInitialLoading(false);
        }
    }, [strategicPlans]);

    const createButton = document.getElementById("saveChanges");
    const editForm = useForm<CustomFormData>({ mode: "onChange" });
    const [isEditFormValid, setEditFormValid] = useState(editForm.formState.isDirty && editForm.formState.isValid);
    const [formData, setFormData] = useState<CustomFormData>();
    const submitForm: SubmitHandler<CustomFormData> = async (data: CustomFormData) => {
        setFormData(data);
    };
    const handleCloseCreateItem = () => setCreateItem(false);
    const handleCloseOnEdit = () => setOnEdit(false);

    function filterMainItemsByName(mainItems: StrategicPlanParent[], search: string): StrategicPlanParent[] {
        return mainItems.filter(mainItem => {
            if (mainItem.name.toLowerCase().includes(search.toLowerCase())) {
                return true;
            }
            return mainItem.items.some(subItem => {
                return subItem.name.toLowerCase().includes(search.toLowerCase()) || hasMatchingSubItem(subItem, search);
            });
        });
    }

    function hasMatchingSubItem(item: StrategicPlanItem, search: string): boolean {
        if (item.children.length === 0) {
            return false;
        }
        return item.children.some(child => {
            return child.name.toLowerCase().includes(search.toLowerCase()) || hasMatchingSubItem(child, search);
        });
    }

    const isDisabledSaveButton = () => {
        if (alertProcessing) {
            return true;
        }
        if (createItem) {
            return !createButton?.classList.contains("ready");
        } else {
            return (!isEditFormValid && !dndChanged && !datesChanged) || !onEdit;
        }
    };

    useEffect(() => {
        setEditFormValid(editForm.formState.isDirty && editForm.formState.isValid);
    }, [editForm.formState.isDirty, editForm.formState.isValid]);

    return (
        <>
            <div className="main-strategic-plan-container">
                <div className="strategic-plan-form">
                    <div className="header-strategic-plan">
                        <h1 className="title-strategic-plan">Institutional Effectiveness Plans</h1>
                        <div className="search-bar-button-container">
                            <div className="search-bar">
                                <DebouncedInput
                                    type="text"
                                    value={searchBarValue}
                                    onChange={(value: string) => {
                                        setSearchBarValue(value.toString());
                                        setPlans(filterMainItemsByName(strategicPlans!, value.toString()));
                                    }}
                                    placeholder="Search by plan name or item name"
                                    className="search-input"
                                    data-test-id="iep-search"
                                />
                                <i className={"type icon icon-search search-icon-bar"} aria-hidden="true" data-testid="icon-search"></i>
                            </div>
                            <Button
                                id="createPlan"
                                data-test-id="iep-create-plan"
                                disabled={createItem || onEdit}
                                onClick={() => { setCreateItem(true); }}
                            >
                                + Create new plan
                            </Button>
                        </div>
                    </div>
                    <div className="color-code-buttons-container">
                        <div className="color-code">
                            <strong>ActionPlan status:</strong>
                            <PlanStatus status="open" />
                            <PlanStatus status="achieved" />
                            <PlanStatus status="unmet" />
                            <PlanStatus status="no_date_assigned" />
                            <PlanStatus status="overdue" />
                        </div>
                        {(createItem || onEdit) &&
                            <div className="save-buttons">
                                <Button
                                    id="cancel"
                                    disabled={!createItem && !onEdit}
                                    onClick={() => setOpenCancel(true)}
                                    className="cancel-button"
                                >
                                    <i className="icon icon-close" />
                                    Cancel
                                </Button>
                                <Button
                                    id="saveChanges"
                                    disabled={isDisabledSaveButton()}
                                    type="submit"
                                    form={createItem ? "new-strategic-plan-form" : "edit-strategic-plan-form"}
                                >
                                    Save changes
                                </Button>
                            </div>
                        }
                    </div>
                    {initialLoading && <div className="text-center"><Spinner animation="border" /></div>}
                    {!initialLoading && (!strategicPlans || strategicPlans && strategicPlans.length === 0) && (
                        <h5 className="text-center">No Institutional Effectiveness Plans available</h5>
                    )}
                    {createItem &&
                        <CreateStrategicPlanForm
                            loadStrategicPlans={loadStrategicPlans}
                            strategicPlanStore={strategicPlanStore}
                            handleCloseCreateItem={handleCloseCreateItem}
                            showAlert={(message: string) => {
                                setAlertContent({ variant: "light", message });
                            }}
                            showProcessing={(isProcessing: boolean) => {
                                setAlertProcessing(isProcessing);
                            }}
                        />
                    }
                    <FormProvider {...editForm}>
                        <form onSubmit={editForm.handleSubmit(submitForm)} id="edit-strategic-plan-form" className="dropdowns-container">
                            {plans?.map((plan: StrategicPlanParent) => (
                                <EditableStrategicPlanForm
                                    loadStrategicPlans={loadStrategicPlans}
                                    formData={formData}
                                    strategicPlan={plan}
                                    handleCloseOnEdit={handleCloseOnEdit}
                                    onEdit={onEdit}
                                    setDndChanged={setDndChanged}
                                    setDatesChanged={setDatesChanged}
                                    key={plan.ruuid}
                                    setOnEdit={() => setOnEdit(true)}
                                    onArchiveItem={onArchiveItem}
                                    onArchivePlan={onArchivePlan}
                                    onDelete={onDelete}
                                    onCopyPlan={onCopyPlan}
                                    showAlert={(message: string) => {
                                        setAlertContent({ variant: "light", message });
                                    }}
                                    showProcessing={(isProcessing: boolean) => {
                                        setAlertProcessing(isProcessing);
                                    }}
                                    disable={createItem || onEdit}
                                />
                            ))}
                        </form>
                    </FormProvider>
                </div>
            </div>
            <StyledModal
                isOpen={openCancel}
                confirmButtonLabel={"Discard changes"}
                cancelButtonLabel={"Cancel"}
                onClose={() => setOpenCancel(false)}
                title={"Discard changes?"}
                subtitle={"If you choose to continue, you will lose any progress you've made."}
                onSubmit={(e) => {
                    e.preventDefault();
                    setOnEdit(false);
                    setCreateItem(false);
                    setOpenCancel(false);
                }}
            />
            {(alertContent.message !== "") && (
                <Alert
                    variant={alertContent.variant}
                    onClose={() => setAlertContent({ variant: "light", message: "" })}
                    dismissible
                    className="alert-strategic-plans"
                >
                    {alertContent.message}
                </Alert>
            )}
            {(alertProcessing) && ( // just ambar: verify
                <Alert
                    variant="light"
                    onClose={(() => { })}
                    dismissible
                    className="alert-strategic-plans"
                >
                    <Spinner size="sm" className="me-2" />Your request is being processed
                </Alert>
            )}
        </>

    );

}